import { memo, useCallback, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type BoardCustomFieldValue, CustomFieldTypeName } from '@air/api/types';

import {
  CustomFieldSelect,
  type CustomFieldSelectProps,
} from '~/components/CustomFields/CustomFieldInput/CustomFieldSelect';
import { chipsToCustomField, chipToCustomField } from '~/components/CustomFields/CustomFieldInput/utils';
import { useGetPublicCustomFieldOptions } from '~/swr-hooks/customFields/useGetPublicCustomFieldOptions';
import { useUpdatePublicItemsCustomFieldValue } from '~/utils/CustomFields/useUpdatePublicItemsCustomFieldValue';

export type PublicBoardCustomFieldSelectProps = {
  field: BoardCustomFieldValue;
  trackingLocation: AirActionTrackingLocation;
  canEditCustomFields: boolean;
  boardId: string;
} & Omit<CustomFieldSelectProps, 'onSelectionChange' | 'cfOptions'>;

export const PublicBoardCustomFieldSelect = memo(
  ({ field, trackingLocation, canEditCustomFields, boardId, ...rest }: PublicBoardCustomFieldSelectProps) => {
    const [shouldLoadOptions, setShouldLoadOptions] = useState(false);
    const { data: cfOptions } = useGetPublicCustomFieldOptions({
      customFieldId: field.id,
      enabled: !shouldLoadOptions,
    });
    const { updatePublicItemsCustomFieldValue } = useUpdatePublicItemsCustomFieldValue();

    const onSelectionChange: CustomFieldSelectProps['onSelectionChange'] = useCallback(
      async ({ addedChips, removedChips, chips }) => {
        if (field.type.title === CustomFieldTypeName.singleSelect) {
          const value = chips.length && !!cfOptions ? chipToCustomField(chips[0], cfOptions.data) : null;

          await updatePublicItemsCustomFieldValue({
            boardIds: [boardId],
            baseCustomField: field,
            nextValue: value,
            trackingLocation,
          });
        } else {
          const selectedFields = chips.length && !!cfOptions ? chipsToCustomField(chips, cfOptions.data) : [];

          await updatePublicItemsCustomFieldValue({
            boardIds: [boardId],
            baseCustomField: field,
            nextValues: selectedFields,
            multiValuesToAdd: addedChips && cfOptions ? chipsToCustomField(addedChips, cfOptions.data) : undefined,
            multiValueIdsToRemove: removedChips?.map((chip) => chip.value),
            trackingLocation,
          });
        }
      },
      [boardId, cfOptions, field, trackingLocation, updatePublicItemsCustomFieldValue],
    );

    return (
      <CustomFieldSelect
        {...rest}
        field={field}
        onSelectionChange={onSelectionChange}
        cfOptions={cfOptions}
        onOptionsOpen={() => setShouldLoadOptions(true)}
        isLoading={shouldLoadOptions && !cfOptions}
        disabled={!canEditCustomFields}
      />
    );
  },
);

PublicBoardCustomFieldSelect.displayName = 'PublicBoardCustomFieldSelect';
